import { HttpContextToken } from '@angular/common/http';

export interface ImpersonationSessionResponse {
  id: string;
  admin_user_id: string;
  admin_email: string;
  target_tenant_id: string;
  target_tenant_name: string;
  target_tenant_slug: string;
  reason: string;
  ticket_ref: string | null;
  started_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_reason: string | null;
}

export type ImpersonationSessionStatus = 'active' | 'expired' | 'revoked';

export interface ImpersonationSessionHistoryItem extends ImpersonationSessionResponse {
  status: ImpersonationSessionStatus;
  action_count: number;
  /** El correo de quien CORTÓ la sesión (`revoked_by`); un core anterior no lo manda. */
  revoked_by_email?: string | null;
}

export interface ImpersonationSessionHistoryResponse {
  items: ImpersonationSessionHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ImpersonationActionItem {
  id: string;
  session_id: string;
  admin_user_id: string;
  target_tenant_id: string;
  agent_id: string | null;
  http_method: string;
  endpoint: string;
  resource_type: string | null;
  resource_id: string | null;
  action_kind: 'create' | 'update' | 'delete' | 'other';
  status_code: number;
  request_metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface ImpersonationHistoryFilters {
  tenant_id?: string;
  admin_id?: string;
  status?: ImpersonationSessionStatus;
  /** Sesiones iniciadas DESDE este instante, inclusive (ISO 8601). */
  started_from?: string;
  /**
   * Sesiones iniciadas ANTES de este instante, **exclusivo** (ISO 8601).
   *
   * El intervalo es semiabierto a propósito: con los dos extremos inclusivos
   * hay que decidir si el último día entra, y esa decisión termina escrita
   * distinto en cada pantalla. Para «hasta el 30» se manda el 1 del mes que
   * viene, y el nombre no se puede leer mal.
   */
  started_before?: string;
  limit?: number;
  offset?: number;
}

export interface StartImpersonationRequest {
  target_tenant_id: string;
  reason: string;
  ticket_ref?: string;
  ttl_minutes?: number;
}

export interface EndImpersonationRequest {
  reason?: string;
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  /*
   * Lo demás que manda `GET /platform/tenants` (core: `TenantSummary` de
   * `platform/infrastructure/schemas.py`). Opcional porque la consola no lo inventa: si un core
   * no lo trae, la fila dice «Sin dato» en esa columna.
   */
  /** The catalogue plan (core WP-18). `null` = no plan; absent = an older core (`PlanDeLaCuenta`). */
  plan_name?: string | null;
  plan_slug?: string | null;
  email?: string;
  /** `trial`, `active`, `past_due` o `canceled` (`VALID_SUBSCRIPTION_STATUSES` del core). */
  subscription_status?: string;
  /** `false` = la plataforma desactivó la cuenta. */
  is_active?: boolean;
  /** Los agentes sin borrar de la cuenta, contados por el servidor. */
  agents_count?: number;
  users_count?: number;
  created_at?: string;
}

export const IMPERSONATION_HEADER = 'X-Impersonation-Session';

/**
 * When this context token is set on an HttpRequest, the global impersonation
 * interceptor will NOT inject the ``X-Impersonation-Session`` header.
 * Used by the impersonation panel itself so listing/starting/ending sessions
 * always runs as the platform admin's own identity, never under a swap.
 *
 * Vive acá y no en `impersonation-api.service.ts` por el bundle inicial: el interceptor de soporte
 * la lee en cada pedido, y mientras la importaba de ese archivo se llevaba al arranque la clase
 * entera del panel de soporte (1,57 kB que nadie usa para pintar la primera pantalla). El servicio
 * la reexporta: quien la importaba de ahí sigue andando.
 */
export const SKIP_IMPERSONATION_HEADER = new HttpContextToken<boolean>(() => false);
export const IMPERSONATION_STORAGE_KEY = 'ab_impersonation_session';

export const IMPERSONATION_ERROR_MARKERS = ['Impersonation session', 'impersonation session'];

export function isImpersonationErrorDetail(detail: unknown): boolean {
  return (
    typeof detail === 'string' &&
    IMPERSONATION_ERROR_MARKERS.some((marker) => detail.includes(marker))
  );
}

/*
 * La duración de una sesión NO está acá: el piso, el techo y la que se propone salen del
 * servidor (`ReglasDeLaSesion`). Había seis duraciones fijas (`TTL_PRESETS`, de 15 min a 8 h), y
 * el servidor recorta lo pedido al techo que se configura en /admin/impersonacion: «8 horas»
 * daba 2. La maqueta pide un número de minutos, y el número se valida contra ese techo.
 */

/*
 * The reason's minimum and the ticket's maximum are not here either: they come from the server
 * (`GET /platform/settings`, `constraints`), with today's numbers only as the fallback for an
 * older core (`ReglasDeLaSesion`, `reglas-de-la-sesion.ts`).
 */

/**
 * Quién cortó la sesión, dicho con un nombre (9.1, 2.º criterio: «el corte queda en el registro
 * con quién lo hizo»). El dato estaba en el servidor —`revoked_by`— y ninguna pantalla lo decía
 * (sexta refutación). `null` si la sesión no se cortó.
 *
 * El correo sale del servidor (`revoked_by_email`). Sin él —un core anterior, o el usuario ya
 * no existe—, si el que cortó es el que la abrió se dice con su correo; si es otro, con su id: un
 * id es un dato, un hueco sin causa no.
 */
export function quienCortoLaSesion(s: ImpersonationSessionHistoryItem): string | null {
  if (!s.revoked_at && s.status !== 'revoked') return null;
  if (s.revoked_by_email) return s.revoked_by_email;
  if (s.revoked_by && s.revoked_by === s.admin_user_id) return `${s.admin_email} (quien la abrió)`;
  if (s.revoked_by) return `el usuario ${s.revoked_by}`;
  return 'sin dato de quién la cortó';
}
